/**
 * CitationLink Component
 *
 * Renders a clickable citation reference that opens the citation panel.
 * Displays document name and page/section reference in a compact format.
 *
 * Requirements: 4.4, 6.4
 */

import React from 'react';
import { DocumentRegular } from '@fluentui/react-icons';
import { Citation } from '../../types/extraction';
import styles from './CitationLink.module.css';

export interface CitationLinkProps {
  /** The citation to display */
  citation: Citation;
  /** Callback when the citation is clicked */
  onClick: (citation: Citation) => void;
  /** Optional index for display (e.g., [1], [2]) */
  index?: number;
  /** Whether to show the compact format (just index) or full format */
  compact?: boolean;
}

/**
 * Formats the page/section reference for display
 */
const formatReference = (citation: Citation): string => {
  const parts: string[] = [];

  if (citation.pageNumber !== undefined) {
    parts.push(`p. ${citation.pageNumber}`);
  }

  if (citation.sectionReference) {
    parts.push(citation.sectionReference);
  }

  return parts.length > 0 ? parts.join(', ') : '';
};

/**
 * Truncates document name if too long
 */
const truncateDocumentName = (name: string, maxLength: number = 30): string => {
  if (name.length <= maxLength) {
    return name;
  }
  return `${name.substring(0, maxLength - 3)}...`;
};

/**
 * CitationLink displays a clickable reference to a source document.
 *
 * The component supports two display modes:
 * - Compact: Shows just the index number (e.g., [1])
 * - Full: Shows document name and page/section reference
 *
 * @example
 * // Compact citation link
 * <CitationLink citation={citation} onClick={handleClick} index={1} compact />
 *
 * @example
 * // Full citation link
 * <CitationLink citation={citation} onClick={handleClick} />
 */
export const CitationLink: React.FC<CitationLinkProps> = ({
  citation,
  onClick,
  index,
  compact = false,
}) => {
  const reference = formatReference(citation);
  const truncatedName = truncateDocumentName(citation.documentName);

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    onClick(citation);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(citation);
    }
  };

  // Build accessible label
  const accessibleLabel = reference
    ? `View citation from ${citation.documentName}, ${reference}`
    : `View citation from ${citation.documentName}`;

  if (compact && index !== undefined) {
    return (
      <button
        type="button"
        className={styles.compactLink}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={accessibleLabel}
        title={`${citation.documentName}${reference ? ` - ${reference}` : ''}`}
      >
        <DocumentRegular className={styles.compactIcon} aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={styles.citationLink}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={accessibleLabel}
    >
      <DocumentRegular className={styles.icon} aria-hidden="true" />
      <span className={styles.content}>
        <span className={styles.documentName}>{truncatedName}</span>
        {reference && (
          <span className={styles.reference}>{reference}</span>
        )}
      </span>
    </button>
  );
};

export default CitationLink;
